import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { TransitionType } from './composition';
import { drawTransition } from './render';

const W = 72;
const H = 44;

function drawA(ctx: CanvasRenderingContext2D) {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#3A3733');
    g.addColorStop(1, '#1C1B19');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.arc(W * 0.3, H * 0.55, H * 0.22, 0, Math.PI * 2);
    ctx.fill();
}

function drawB(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = '#D9D3C7';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(60,55,50,0.35)';
    ctx.lineWidth = 1;
    for (let x = -H; x < W; x += 7) {
        ctx.beginPath();
        ctx.moveTo(x, H);
        ctx.lineTo(x + H, 0);
        ctx.stroke();
    }
}

/**
 * A transition, drawn by the real renderer between two swatches. Still at
 * the midpoint; plays on hover so the vendor sees the motion before choosing.
 */
export const TransitionTile: React.FC<{
    type: TransitionType;
    label: string;
    active?: boolean;
    onClick: () => void;
}> = ({ type, label, active, onClick }) => {
    const ref = useRef<HTMLCanvasElement>(null);
    const [hover, setHover] = useState(false);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return undefined;
        const paint = (p: number) => {
            ctx.save();
            ctx.clearRect(0, 0, W, H);
            drawTransition(ctx, type, p, () => drawA(ctx), () => drawB(ctx), W, H);
            ctx.restore();
        };
        if (!hover) {
            // Just past the start, so dips and wipes read as what they are
            // (a flash still shows the picture blowing out, not a white tile).
            paint(type === 'fade-black' || type === 'fade-white' ? 0.3 : type === 'flash' ? 0.22 : 0.45);
            return undefined;
        }
        let raf = 0;
        const t0 = performance.now();
        const loop = () => {
            const cycle = ((performance.now() - t0) / 1400) % 1;
            // Hold on each end so the motion reads.
            const p = cycle < 0.15 ? 0 : cycle > 0.85 ? 1 : (cycle - 0.15) / 0.7;
            paint(p);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [type, hover]);

    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            aria-pressed={active}
            className={cn(
                'flex flex-col items-center gap-1.5 rounded-lg border p-1.5 text-[11px] transition-colors hover:bg-muted',
                active ? 'border-foreground text-foreground outline outline-[0.5px] outline-foreground' : 'border-border text-muted-foreground',
            )}
        >
            <canvas ref={ref} width={W} height={H} className="h-[44px] w-full rounded-md" />
            <span className="truncate">{label}</span>
        </button>
    );
};
